import IconAttachment from "@shared/components/icons/Attachment"
import IconCloudUpload from "@shared/components/icons/CloudUpload"
import IconEllipsis from "@shared/components/icons/Ellipsis"
import IconGoogleDrive from "@shared/components/icons/GoogleDrive"
import IconHeadingBig from "@shared/components/icons/HeadingBig"
import IconHeadingSmall from "@shared/components/icons/HeadingSmall"
import IconInsertImageBottom from "@shared/components/icons/InsertImageBottom"
import IconList from "@shared/components/icons/List"
import IconListBullets1 from "@shared/components/icons/ListBullets1"
import IconListNumbers from "@shared/components/icons/ListNumbers"
import IconListStars from "@shared/components/icons/ListStars"
import IconMessagesBubbleSquareAdd from "@shared/components/icons/MessagesBubbleSquareAdd"
import IconMicrosoftOnedrive from "@shared/components/icons/MicrosoftOnedrive"
import IconNetworkArrow from "@shared/components/icons/NetworkArrow"
import IconPictureLandscape from "@shared/components/icons/PictureLandscape"
import IconScaleVertical from "@shared/components/icons/ScaleVertical"
import IconTagsSearch from "@shared/components/icons/TagsSearch"
import type { IconComponent } from "@shared/components/menus/DrilldownMenu"
import {
  DisclosureChevron,
  DisclosureRow,
  DrilldownMenu,
  DrilldownRow,
  HeaderWithBackArrow,
  MenuHeader,
  MenuRoot,
  MenuTitle,
  Row,
  RowContent,
  RowIcon,
  RowLabel,
  Rows,
} from "@shared/components/menus/DrilldownMenu"
import { cx } from "@shared/components/untitledui/util/cx"
import {
  getCardId as cardIdOfNode,
  createTitledCard,
  nextCardColor,
} from "@shared/unified-tiptap/util/card"
import { tracked } from "@supergrain/kernel/react"
import type { ChainedCommands, Editor, JSONContent } from "@tiptap/core"
import { createNodeFromContent } from "@tiptap/core"
import { GapCursor } from "@tiptap/pm/gapcursor"
import { Fragment, Node as PMNode, Slice } from "@tiptap/pm/model"
import { TextSelection } from "@tiptap/pm/state"
import type { ReactNode } from "react"
import { useRef, useState } from "react"
import { cardIdForMenuAnchor, reportMenuOpenChange } from "../../analytics/open-menu-event"
import { OPERATION_CONTRACTS, type OperationContract } from "../../analytics/operations"
import {
  asAttachmentId,
  asImageId,
  asStandardId,
  type EditorBlockKind,
  type PickerAttachmentSource,
} from "../../analytics/types"
import type { Link } from "../../extensions/link/link-attrs"
import { linkFromGooglePick, linkFromUpload } from "../../extensions/link/link-attrs"
import {
  deleteStandardsByIdsCommand,
  extractLessonStandards,
} from "../../extensions/standard/standard-extension"
import { useUiContext } from "../../unified-tiptap-ui-context"
import { findCardDepth, isTargetableWidget } from "../../util/find-add-target-pos"
import { AddButtonLinkDialog } from "../add-dialogs/AddButtonLinkDialog"
import { AddCommentView } from "../comments/AddCommentView"
import { useSubmitCardComment } from "../comments/use-submit-card-comment"
import { useMountedOnceOpened } from "../use-mounted-once-opened"

export type AddMenuItemId =
  | "STANDARDS"
  | "FILE"
  | "UPLOAD"
  | "GOOGLE_DRIVE"
  | "ONE_DRIVE"
  | "OTHER"
  | "LINK"
  | "PICTURE"
  | "CARD"
  | "COMMENT"
  | "LISTS"
  | "BULLETED_LIST"
  | "NUMBERED_LIST"
  | "CHECKLIST"
  | "HEADING"
  | "HEADING_1"
  | "HEADING_2"

export interface AddButtonProps {
  editor: Editor
  /**
   * Which surface owns this Add button, which decides the menu it offers (default
   * `"titledCard"`):
   * - `"titledCard"` — the full menu (Tag, Attachment, Organization, Formatting). Also used by untitledCard.
   * - `"cardGoogleClassroom"` — only the Attachment section and the Card option; the
   *   Google Classroom card manages everything else in its sidebar.
   * - `"lessonTitle"` — only the Card option. Its `getAddTargetPos` should point at
   *   the lessonTitle node, so the new card is inserted right after it — the first card,
   *   regardless of how many cards already exist (including zero).
   * - `"dateAnnotationNote"` — note menu excluding card, comment, and standards by default.
   * - `"commentContent"` — the same menu a note offers, for the card-comment editor.
   */
  mode:
    | "titledCard"
    | "cardGoogleClassroom"
    | "lessonTitle"
    | "dateAnnotationNote"
    | "commentContent"
  /**
   * Items to exclude from the Add menu by ID.
   */
  excludedItems?: Array<AddMenuItemId>
  /**
   * Function that returns the (first inner) position of the node to add to.
   * If null when the user chooses the content to add, adding is skipped.
   *
   * Must be an output of findAddTargetPos.
   */
  getAddTargetPos: () => number | null
  /**
   * Function that returns the id of the card this Add menu belongs to, or null if it
   * can't be determined (e.g. the card is no longer in the doc). Used by the Comment
   * option, which attaches its comment to that card.
   */
  getCardId: () => string | null
  /**
   * Callback when the dropdown opens or closes.
   */
  onOpenChange?: (isOpen: boolean) => void
  /**
   * Handle clicking the "Link" button.
   *
   * By default, opens the AddButtonLinkDialog.
   */
  addLink?: () => void
  /**
   * When set, attachment picks (File / Link / Picture) are delivered here as
   * Links instead of being inserted as block nodes. Used by the Google Classroom
   * card, whose attachments live in its node attrs rather than as ProseMirror
   * children.
   */
  addAttachment?: (link: Link) => void
  /** The button that opens the menu. */
  children: ReactNode
}

interface AddBlocksOptions {
  /** Whether the new content should replace an empty P in the parent if present (default: true). */
  replaceP?: boolean
  /** The cursor position to set in the last block (default: 1, the first position inside it). */
  cursorPos?: number
}

/**
 * A dropdown menu for adding cards and blocks to the editor.
 */
export const AddButton = tracked(
  ({
    editor,
    mode,
    excludedItems,
    getAddTargetPos,
    getCardId,
    onOpenChange,
    addLink,
    addAttachment,
    children,
  }: AddButtonProps) => {
    // TODO: Be careful for async adds (standards, files, images).
    // addPos may move. Use ElementIDs instead?

    const {
      permissions,
      isNonInstructional,
      filestackClient,
      openStandardsModal,
      addFromGoogleDrive,
    } = useUiContext()
    /** Store the comment the Comment view composed, against the card the menu belongs to. */
    const submitComment = useSubmitCardComment(editor, getCardId)

    const uploadInputRef = useRef<HTMLInputElement>(null)
    const handleOnOpenChange = (open: boolean) => {
      onOpenChange?.(open)
      // The Add menu's own open edge, reported from the one component every host mounts, so no
      // host can add a trigger that opens the menu without reporting it.
      reportMenuOpenChange(editor, "add-button", open, () =>
        cardIdForMenuAnchor(editor, getAddTargetPos()),
      )
      // Restore focus to the editor when the menu closes (matching CardMenu): left to
      // react-aria, focus would return to the trigger pill, whose focus-within pins the
      // card's hover chrome up until the user clicks somewhere else. Without
      // scrollIntoView: the selection can be anywhere in the document, and scrolling it
      // into view on dismiss yanks the card out from under the pointer.
      if (!open) editor.commands.focus(null, { scrollIntoView: false })
    }

    const [openDialog, setOpenDialog] = useState<"link" | null>(null)
    // The dialog stays out of the tree until the row that opens it is used — there is one
    // AddButton per card, and almost none of them ever opens it.
    const linkDialogMounted = useMountedOnceOpened(openDialog === "link")

    /** Insert an empty titledCard after the Add menu's card. */
    const addCard = () => {
      // Find the position after the deepest card containing the addTargetPos. When the
      // target points AT a card node (a cardGoogleClassroom, whose widget-style target
      // pos is the node's own position) or AT the lessonTitle (the lesson-title Add button,
      // which drops a card directly below the title), that node is the one to insert after.
      const addTargetPos = getAddTargetPos()
      if (addTargetPos === null) return
      const $addTargetPos = editor.state.doc.resolve(addTargetPos)

      let addCardPos: number
      const nodeAtTarget = $addTargetPos.nodeAfter
      if (nodeAtTarget?.type.isInGroup("card") || nodeAtTarget?.type.name === "lessonTitle") {
        addCardPos = addTargetPos + nodeAtTarget.nodeSize
      } else {
        const cardDepth = findCardDepth($addTargetPos)
        if (cardDepth === null) return
        addCardPos = $addTargetPos.after(cardDepth)
      }

      const card = createTitledCard(editor.schema, nextCardColor(editor.state.doc))
      const cardId = cardIdOfNode(card)
      if (cardId === null) return

      editor
        .chain()
        .label(OPERATION_CONTRACTS.OP_ADD_CARD, "add-button", { cardId })
        .insertContentAt(addCardPos, card)
        .command(({ tr }) => {
          // The card arrives untitled, so the cursor goes in its title rather than its body.
          tr.setSelection(TextSelection.near(tr.doc.resolve(addCardPos + 1), 1))
          return true
        })
        .focus()
        .run()
    }

    /**
     * Insert these blocks at the Add menu's target position — a no-op when that position no
     * longer resolves. `operation`/`args` label the transaction, so the insertion reports that
     * operation, and the chain runs here. A caller with more to do in the same transaction instead
     * passes its own `chain`, and owns calling `run()` on it.
     */
    function addBlocks<Args>(
      blockContents: Array<JSONContent | PMNode>,
      options: AddBlocksOptions & { operation: OperationContract<Args>; args: NoInfer<Args> },
    ): void
    function addBlocks(
      blockContents: Array<JSONContent | PMNode>,
      options: AddBlocksOptions & { chain: ChainedCommands },
    ): void
    function addBlocks(
      blockContents: Array<JSONContent | PMNode>,
      options: AddBlocksOptions & {
        operation?: OperationContract<unknown>
        args?: unknown
        chain?: ChainedCommands
      },
    ): void {
      const addTargetPos = getAddTargetPos()
      if (addTargetPos === null) return

      // Find the position to append to.
      // For a widget or block, it is just the position after that node.
      // For a card or other block container, it is the position at the end of its content.
      // Also find the last maybe-block node before the endPos, if relevant.

      let addPos: number
      let cardEndBlock: PMNode | null = null
      if (isTargetableWidget(editor, addTargetPos)) {
        addPos = addTargetPos + 1
      } else {
        const $addTargetPos = editor.state.doc.resolve(addTargetPos)
        if (
          $addTargetPos.parent.type.isInGroup("card") ||
          $addTargetPos.parent.type.isInGroup("blockContainer")
        ) {
          addPos = $addTargetPos.end($addTargetPos.depth)
          cardEndBlock = $addTargetPos.parent.lastChild
        } else {
          addPos = $addTargetPos.after($addTargetPos.depth)
          cardEndBlock = $addTargetPos.parent
        }
      }

      const blockNodes = blockContents.map(
        (content) => createNodeFromContent(content, editor.schema) as PMNode,
      )
      const lastBlock = blockNodes[blockNodes.length - 1]
      const lastBlockIsWidget = lastBlock.type.isInGroup("widget")

      const chain = options.chain ?? editor.chain()
      if (options.operation) chain.label(options.operation, "add-button", options.args)
      let adjustedAddPos: number

      // If adding to a card and the last block in the card is an empty P (e.g. the initial placeholder), replace it.
      if (
        (options.replaceP ?? true) &&
        cardEndBlock &&
        cardEndBlock.type.name === "paragraph" &&
        cardEndBlock.content.size === 0
      ) {
        chain.command(({ tr }) => {
          tr.replaceRange(
            addPos - cardEndBlock.nodeSize,
            addPos,
            new Slice(Fragment.from(blockNodes), 0, 0),
          )
          return true
        })
        adjustedAddPos = addPos - cardEndBlock.nodeSize
      } else {
        chain.insertContentAt(addPos, Fragment.from(blockNodes))
        adjustedAddPos = addPos
      }

      chain
        .command(({ tr }) => {
          // Set a cursor near the last added block.
          let lastBlockPos = adjustedAddPos
          for (let i = 0; i < blockNodes.length - 1; i++) lastBlockPos += blockNodes[i].nodeSize

          if (lastBlockIsWidget) {
            // For widget blocks, select a GapCursor at the end.
            tr.setSelection(new GapCursor(tr.doc.resolve(lastBlockPos + 1)))
          } else {
            // For normal content, select the first position inside the block,
            // unless adjusted by options.cursorPos.
            tr.setSelection(
              TextSelection.near(tr.doc.resolve(lastBlockPos + (options.cursorPos ?? 1))),
            )
          }
          return true
        })
        .focus()
      if (!options.chain) chain.run()
    }

    /**
     * Insert one formatting block from the Add menu. The menu label, the schema node name and the
     * reported block kind are three vocabularies for the same thing, so each menu item names all
     * three here rather than letting them drift apart.
     */
    const insertBlock = (block: EditorBlockKind, content: JSONContent) => {
      const addTargetPos = getAddTargetPos()
      // Nowhere to add: bail before the chain, so no label reports an insertion that never happened.
      if (addTargetPos === null) return
      const cardId = cardIdForMenuAnchor(editor, addTargetPos)
      // Without a card there is nothing to name in the args, so the insertion goes unlabeled.
      const chain = editor.chain()
      if (cardId) chain.label(OPERATION_CONTRACTS.OP_INSERT_BLOCK, "add-button", { block, cardId })
      addBlocks([content], { chain })
      chain.run()
    }

    /**
     * Open the standards search modal, then apply the user's changes as ONE mutation: insert each
     * added standard as a widget block and delete each removed standard by id. We diff the
     * standards the user chose against the lesson's current standards to find what was added and
     * removed.
     *
     * Both halves commit in a single transaction, so the whole picker session carries one event
     * holding both id sets rather than an add event plus a remove event.
     */
    const addStandards = async () => {
      if (!openStandardsModal) return
      const before = extractLessonStandards(editor.state.doc)
      const cardId = cardIdForMenuAnchor(editor, getAddTargetPos())
      const after = await openStandardsModal(before)

      const beforeIds = new Set(before.map((standard) => standard.id))
      const afterIds = new Set(after.map((standard) => standard.id))
      const added = after.filter((standard) => !beforeIds.has(standard.id))
      const removed = before.filter((standard) => !afterIds.has(standard.id))

      // The card can go away while the picker is open, which is what makes an unresolvable add
      // target reachable here. The removals are lesson-wide and must still land, so the mutation
      // carries whichever halves survive — and the args state what it really carried.
      const isAdding = added.length > 0 && getAddTargetPos() !== null
      const addedIds = isAdding ? added.map((standard) => asStandardId(standard.id)) : []
      const removedIds = removed.map((standard) => asStandardId(standard.id))
      if (addedIds.length === 0 && removedIds.length === 0) return

      // The insertion goes first because its position was computed against the pre-edit document,
      // while the deletion finds its own positions in the transaction as it runs.
      const chain = editor.chain().label(OPERATION_CONTRACTS.OP_EDIT_STANDARDS, "add-button", {
        cardId,
        addedIds,
        removedIds,
      })
      if (isAdding) {
        addBlocks(
          added.map((standard) => editor.schema.node("standard", { standard })),
          { chain },
        )
      }
      chain.command(deleteStandardsByIdsCommand(new Set(removedIds))).run()
    }

    /** Insert picked files as linkBlock tokens, under one `insert-file` naming every attachment. */
    const insertAttachments = (links: Link[]) => {
      const [first, ...rest] = links
      if (first === undefined) return
      addBlocks(
        links.map((attrs) => ({ type: "linkBlock", attrs })),
        {
          operation: OPERATION_CONTRACTS.OP_INSERT_FILE,
          args: {
            kind: "attachment",
            fileIds: [
              asAttachmentId(first.attachmentId),
              ...rest.map((link) => asAttachmentId(link.attachmentId)),
            ],
            // One picker session has one source; a mixed pick reports its first file's.
            source: first.source === "link" ? "other" : first.source,
          },
        },
      )
    }

    // Open the Filestack picker, inserting each uploaded image as a block (or delivering
    // it as an attachment when addAttachment is set).
    const addImages = () => {
      if (!filestackClient) return
      filestackClient
        .picker({
          fromSources: [
            "local_file_system",
            "imagesearch",
            "dropbox",
            "googledrive",
            "webcam",
            "onedrive",
            "onedriveforbusiness",
          ],
          maxFiles: 20,
          accept: ["image/*"],
          onUploadDone: (res) => {
            if (addAttachment) {
              res.filesUploaded.forEach((file) =>
                addAttachment(linkFromUpload(file, attachmentSourceFromPicker(file.source))),
              )
            } else {
              const [first, ...rest] = res.filesUploaded
              if (first === undefined) return
              addBlocks(
                res.filesUploaded.map((file) => ({ type: "image", attrs: { src: file.url } })),
                {
                  operation: OPERATION_CONTRACTS.OP_INSERT_FILE,
                  args: {
                    kind: "image",
                    // The image node stores no id of its own, so the picked images are named by
                    // their Filestack handle — the identity of the file the src points at.
                    fileIds: [
                      asImageId(first.handle),
                      ...rest.map((file) => asImageId(file.handle)),
                    ],
                    source: attachmentSourceFromPicker(first.source),
                  },
                },
              )
            }
          },
        })
        .open()
    }

    const addFilesFrom = (fromSources: string[]) => {
      if (!filestackClient) return
      filestackClient
        .picker({
          fromSources,
          maxFiles: 20,
          onUploadDone: (res) => {
            if (addAttachment) {
              res.filesUploaded.forEach((file) =>
                addAttachment(linkFromUpload(file, attachmentSourceFromPicker(file.source))),
              )
            } else {
              insertAttachments(
                res.filesUploaded.map((file) =>
                  linkFromUpload(file, attachmentSourceFromPicker(file.source)),
                ),
              )
            }
          },
        })
        .open()
    }

    const openNativeUpload = () => {
      const input = uploadInputRef.current
      if (!input) return
      input.value = ""
      input.click()
    }

    const uploadNativeFiles = async (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0 || !filestackClient) return

      const result: unknown = await filestackClient.multiupload([...fileList])
      if (!Array.isArray(result)) return
      const files = result.filter(isUploadedFile)

      if (addAttachment) {
        files.forEach((file) => addAttachment(linkFromUpload(file, "upload")))
      } else {
        insertAttachments(files.map((file) => linkFromUpload(file, "upload")))
      }
    }

    const addMicrosoftOnedrive = () => addFilesFrom(["onedrive", "onedriveforbusiness"])
    const addOtherFiles = () =>
      addFilesFrom([
        "imagesearch",
        "webcam",
        "video",
        "audio",
        "instagram",
        "gmail",
        "facebook",
        "unsplash",
      ])

    // Open the host app's Google Drive picker (addFromGoogleDrive prop), inserting each picked
    // file as a linkBlock token — or delivering it as an attachment when addAttachment is set.
    const handleAddFromGoogleDrive = async () => {
      if (!addFromGoogleDrive) return

      const files = await addFromGoogleDrive()
      if (files.length === 0) return

      if (addAttachment) {
        files.forEach((file) => addAttachment(linkFromGooglePick(file)))
      } else {
        insertAttachments(files.map((file) => linkFromGooglePick(file)))
      }
    }

    const handleAddLink = () => {
      if (addLink) addLink()
      else setOpenDialog("link")
    }

    const showCard = editor.isEditable && !isNonInstructional
    const hasOrganizationRows = showCard || permissions.canComment
    // Every attachment row uploads through Filestack, so without a client there is nothing
    // to offer.
    const showAttachments = editor.isEditable && filestackClient !== undefined
    const showTag = editor.isEditable && openStandardsModal !== undefined

    return (
      <>
        <DrilldownMenu
          ariaLabel="Add menu"
          trigger={children}
          className="w-62 pt-2"
          header={
            <MenuHeader className="border-b border-border-secondary">
              <MenuTitle>Add Menu</MenuTitle>
            </MenuHeader>
          }
          onOpenChange={handleOnOpenChange}
        >
          {({ close }) => {
            const isExcluded = (id: AddMenuItemId) => {
              if (excludedItems) return excludedItems.includes(id)
              if (mode === "dateAnnotationNote" || mode === "commentContent") {
                return id === "CARD" || id === "COMMENT" || id === "STANDARDS"
              }
              return false
            }

            const closeThen = (action: () => void) => () => {
              close()
              action()
            }
            const cardRow =
              showCard && !isExcluded("CARD") ? (
                <Row
                  className={ADD_MENU_ROW_CLASS}
                  typeaheadText="Card"
                  onAction={closeThen(addCard)}
                >
                  <AddRowFace icon={IconInsertImageBottom}>Card</AddRowFace>
                </Row>
              ) : null
            const commentRow =
              permissions.canComment && !isExcluded("COMMENT") ? (
                <DrilldownRow
                  className={ADD_MENU_ROW_CLASS}
                  typeaheadText="Comment"
                  header={({ back }) => (
                    <HeaderWithBackArrow backTo="Add Menu" onBack={back}>
                      Add Comment
                    </HeaderWithBackArrow>
                  )}
                  body={({ back, close: closeMenu }) => (
                    <AddCommentView
                      draftKey={getCardId()}
                      onCancel={back}
                      onSubmit={(commentText) => {
                        submitComment(commentText)
                        closeMenu()
                      }}
                    />
                  )}
                >
                  <AddRowFace icon={IconMessagesBubbleSquareAdd}>Comment</AddRowFace>
                </DrilldownRow>
              ) : null
            const organizationRows = (
              <>
                {commentRow}
                {cardRow}
              </>
            )

            const fileRow = !isExcluded("FILE") ? (
              <DisclosureRow
                typeaheadText="File"
                className={ADD_MENU_ROW_CLASS}
                face={
                  <AddRowFace icon={IconAttachment} trailing={<DisclosureChevron />}>
                    File
                  </AddRowFace>
                }
              >
                <Rows className="pt-0 pb-0.5">
                  {!isExcluded("UPLOAD") && (
                    <Row
                      className={ADD_MENU_ROW_CLASS}
                      typeaheadText="Upload"
                      onAction={closeThen(openNativeUpload)}
                    >
                      <AddRowFace icon={IconCloudUpload}>Upload</AddRowFace>
                    </Row>
                  )}
                  {addFromGoogleDrive && !isExcluded("GOOGLE_DRIVE") ? (
                    <Row
                      className={ADD_MENU_ROW_CLASS}
                      typeaheadText="Google Drive"
                      onAction={closeThen(handleAddFromGoogleDrive)}
                    >
                      <AddRowFace icon={IconGoogleDrive}>Google Drive</AddRowFace>
                    </Row>
                  ) : null}
                  {!isExcluded("ONE_DRIVE") && (
                    <Row
                      className={ADD_MENU_ROW_CLASS}
                      typeaheadText="Microsoft OneDrive"
                      onAction={closeThen(addMicrosoftOnedrive)}
                    >
                      <AddRowFace icon={IconMicrosoftOnedrive}>Microsoft OneDrive</AddRowFace>
                    </Row>
                  )}
                  {!isExcluded("OTHER") && (
                    <Row
                      className={ADD_MENU_ROW_CLASS}
                      typeaheadText="Other"
                      onAction={closeThen(addOtherFiles)}
                    >
                      <AddRowFace icon={IconEllipsis}>Other</AddRowFace>
                    </Row>
                  )}
                </Rows>
              </DisclosureRow>
            ) : null

            const attachmentRows = (
              <>
                {fileRow}
                {!isExcluded("LINK") && (
                  <Row
                    className={ADD_MENU_ROW_CLASS}
                    typeaheadText="Link"
                    onAction={closeThen(handleAddLink)}
                  >
                    <AddRowFace icon={IconNetworkArrow}>Link</AddRowFace>
                  </Row>
                )}
                {!isExcluded("PICTURE") && (
                  <Row
                    className={ADD_MENU_ROW_CLASS}
                    typeaheadText="Picture"
                    onAction={closeThen(addImages)}
                  >
                    <AddRowFace icon={IconPictureLandscape}>Picture</AddRowFace>
                  </Row>
                )}
              </>
            )

            const formattingRows = (
              <>
                {!isExcluded("LISTS") && (
                  <DisclosureRow
                    typeaheadText="Lists"
                    className={ADD_MENU_ROW_CLASS}
                    face={
                      <AddRowFace
                        icon={IconList}
                        iconClassName="text-amber-700"
                        trailing={<DisclosureChevron />}
                      >
                        Lists
                      </AddRowFace>
                    }
                  >
                    <Rows className="pt-0 pb-0.5">
                      {!isExcluded("BULLETED_LIST") && (
                        <Row
                          className={ADD_MENU_ROW_CLASS}
                          typeaheadText="Bulleted List"
                          onAction={closeThen(() =>
                            insertBlock("bullet-list", { type: "flatListItemUnordered" }),
                          )}
                        >
                          <AddRowFace icon={IconListStars}>Bulleted List</AddRowFace>
                        </Row>
                      )}
                      {!isExcluded("NUMBERED_LIST") && (
                        <Row
                          className={ADD_MENU_ROW_CLASS}
                          typeaheadText="Numbered List"
                          onAction={closeThen(() =>
                            insertBlock("numbered-list", { type: "flatListItemOrdered" }),
                          )}
                        >
                          <AddRowFace icon={IconListNumbers}>Numbered List</AddRowFace>
                        </Row>
                      )}
                      {!isExcluded("CHECKLIST") && (
                        <Row
                          className={ADD_MENU_ROW_CLASS}
                          typeaheadText="Checklist"
                          onAction={closeThen(() =>
                            insertBlock("checklist", { type: "flatListItemTask" }),
                          )}
                        >
                          <AddRowFace icon={IconListBullets1}>Checklist</AddRowFace>
                        </Row>
                      )}
                    </Rows>
                  </DisclosureRow>
                )}

                {!isExcluded("HEADING") && (
                  <DisclosureRow
                    typeaheadText="Heading"
                    className={ADD_MENU_ROW_CLASS}
                    face={
                      <AddRowFace icon={IconScaleVertical} trailing={<DisclosureChevron />}>
                        Heading
                      </AddRowFace>
                    }
                  >
                    <Rows className="pt-0 pb-0.5">
                      {!isExcluded("HEADING_1") && (
                        <Row
                          className={ADD_MENU_ROW_CLASS}
                          typeaheadText="Heading 1"
                          onAction={closeThen(() =>
                            insertBlock("heading", { type: "heading", attrs: { level: 1 } }),
                          )}
                        >
                          <AddRowFace icon={IconHeadingBig}>Heading 1</AddRowFace>
                        </Row>
                      )}
                      {!isExcluded("HEADING_2") && (
                        <Row
                          className={ADD_MENU_ROW_CLASS}
                          typeaheadText="Heading 2"
                          onAction={closeThen(() =>
                            insertBlock("heading", { type: "heading", attrs: { level: 2 } }),
                          )}
                        >
                          <AddRowFace icon={IconHeadingSmall}>Heading 2</AddRowFace>
                        </Row>
                      )}
                    </Rows>
                  </DisclosureRow>
                )}
              </>
            )

            const menuSections = () => {
              switch (mode) {
                case "lessonTitle":
                  return <AddMenuSection>{cardRow}</AddMenuSection>
                case "cardGoogleClassroom":
                  return (
                    <>
                      {showAttachments ? (
                        <AddMenuSection title="Attachment">{attachmentRows}</AddMenuSection>
                      ) : null}
                      {hasOrganizationRows ? (
                        <AddMenuSection title="Organization" separated={showAttachments}>
                          {organizationRows}
                        </AddMenuSection>
                      ) : null}
                    </>
                  )
                case "dateAnnotationNote":
                case "commentContent":
                case "titledCard":
                default:
                  return (
                    <>
                      {showTag && !isExcluded("STANDARDS") ? (
                        <AddMenuSection title="Tag">
                          <Row
                            className={ADD_MENU_ROW_CLASS}
                            typeaheadText="Standards"
                            onAction={closeThen(addStandards)}
                          >
                            <AddRowFace icon={IconTagsSearch}>Standards</AddRowFace>
                          </Row>
                        </AddMenuSection>
                      ) : null}
                      {showAttachments ? (
                        <AddMenuSection
                          title="Attachment"
                          separated={showTag && !isExcluded("STANDARDS")}
                        >
                          {attachmentRows}
                        </AddMenuSection>
                      ) : null}
                      {hasOrganizationRows && (cardRow || commentRow) ? (
                        <AddMenuSection
                          title="Organization"
                          separated={(showTag && !isExcluded("STANDARDS")) || showAttachments}
                        >
                          {organizationRows}
                        </AddMenuSection>
                      ) : null}
                      {editor.isEditable && (!isExcluded("LISTS") || !isExcluded("HEADING")) ? (
                        <AddMenuSection title="Formatting" separated>
                          {formattingRows}
                        </AddMenuSection>
                      ) : null}
                    </>
                  )
              }
            }

            return <MenuRoot className="gap-0 pb-2">{menuSections()}</MenuRoot>
          }}
        </DrilldownMenu>

        <input
          ref={uploadInputRef}
          type="file"
          multiple
          hidden
          onChange={(event) => void uploadNativeFiles(event.currentTarget.files)}
        />

        {linkDialogMounted && (
          <AddButtonLinkDialog
            editor={editor}
            open={openDialog === "link"}
            onOpenChange={(open) => !open && setOpenDialog(null)}
            addToCard={(node, link) => {
              if (addAttachment) {
                // The Google Classroom card's attachments live in the sidebar's domain; its
                // adapter reports insert-google-classroom-attachment itself.
                addAttachment(link)
                return
              }
              addBlocks([node], {
                operation: OPERATION_CONTRACTS.OP_INSERT_LINK,
                args: { attachmentId: asAttachmentId(link.attachmentId), url: link.url },
              })
            }}
          />
        )}
      </>
    )
  },
)

function AddMenuSection({
  title,
  children,
  separated = false,
}: {
  title?: string
  children: ReactNode
  separated?: boolean
}) {
  return (
    <>
      {separated ? <div className="my-1 h-px w-full bg-border-secondary" /> : null}
      {/* px, not pl: the chevron on a disclosure row needs the same air off the menu's right
          edge that the icon column gets on its left. */}
      <section className="px-3 py-1">
        {title ? <h3 className="m-0 px-2.5 pb-0.5 text-lg font-bold">{title}</h3> : null}
        <Rows className="py-0 pl-0">{children}</Rows>
      </section>
    </>
  )
}

const ADD_MENU_ROW_CLASS = "px-2.5 py-1.5"

function AddRowFace({
  icon,
  iconClassName,
  trailing,
  children,
}: {
  icon: IconComponent
  iconClassName?: string
  trailing?: ReactNode
  children: ReactNode
}) {
  return (
    <RowContent className="gap-2">
      <RowIcon
        icon={icon}
        className={cx(
          "ml-0 size-5 opacity-70 transition-opacity group-data-hovered:opacity-100",
          iconClassName,
        )}
      />
      {/* Every row here names something to insert, not something to do to the card. */}
      <RowLabel kind="content">{children}</RowLabel>
      {trailing}
    </RowContent>
  )
}

/**
 * Maps a Filestack picker source to the Link provenance value (docs/unified-links.md
 * §6): OneDrive keeps its own value; a local pick is a plain upload; everything else
 * (webcam, gmail, unsplash, …) is "other". Google Drive and Dropbox have their own
 * pickers, not Filestack sources.
 */
function attachmentSourceFromPicker(pickerSource: string): PickerAttachmentSource {
  if (pickerSource === "local_file_system") return "upload"
  if (pickerSource === "onedrive" || pickerSource === "onedriveforbusiness") return "onedrive"
  return "other"
}

type UploadedFile = Parameters<typeof linkFromUpload>[0]

function isUploadedFile(value: unknown): value is UploadedFile {
  return (
    typeof value === "object" &&
    value !== null &&
    "url" in value &&
    typeof value.url === "string" &&
    "filename" in value &&
    typeof value.filename === "string" &&
    (!("mimetype" in value) ||
      value.mimetype === undefined ||
      typeof value.mimetype === "string") &&
    (!("size" in value) || value.size === undefined || typeof value.size === "number")
  )
}
